import { useState } from 'react';

// Utility function to extract initials from fullName
const getInitials = (fullName: string): string => {
  if (!fullName) return '?';

  const nameParts = fullName.trim().split(/\s+/);

  if (nameParts.length === 1) {
    // Single name - take first two characters
    return nameParts[0].substring(0, 2).toUpperCase();
  } else {
    // Multiple names - take first letter of first and last name
    const firstInitial = nameParts[0].charAt(0);
    const lastInitial = nameParts[nameParts.length - 1].charAt(0);
    return (firstInitial + lastInitial).toUpperCase();
  }
};

// Generate consistent background color based on fullName
const getAvatarColor = (fullName: string): string => {
  const colors = [
    '#FF6B6B',
    '#4ECDC4',
    '#45B7D1',
    '#96CEB4',
    '#FFEAA7',
    '#DDA0DD',
    '#98D8C8',
    '#F7DC6F',
    '#BB8FCE',
    '#85C1E9'
  ];

  let hash = 0;
  for (let i = 0; i < fullName.length; i++) {
    hash = fullName.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
};

interface UseAvatarProps {
  fullName: string;
  uid?: string;
  baseImageUrl?: string;
}

interface UseAvatarReturn {
  imageUrl: string | null;
  initials: string;
  backgroundColor: string;
  hasImageError: boolean;
  handleImageError: () => void;
  resetImageError: () => void;
}

/**
 * Custom hook for avatar functionality
 * Handles image loading, fallback initials, and consistent colors
 */
export function useAvatar({
  fullName,
  uid,
  baseImageUrl
}: UseAvatarProps): UseAvatarReturn {
  const [hasImageError, setHasImageError] = useState(false);

  // Generate image URL if uid is provided
  const imageUrl =
    uid && baseImageUrl
      ? `${baseImageUrl}/${uid}`
      : uid
      ? `https://s7d2.scene7.com/is/image/IMGDIR/${uid}`
      : null;

  // Generate initials and color
  const initials = getInitials(fullName);
  const backgroundColor = getAvatarColor(fullName);

  const handleImageError = () => {
    setHasImageError(true);
  };

  const resetImageError = () => {
    setHasImageError(false);
  };

  return {
    imageUrl,
    initials,
    backgroundColor,
    hasImageError,
    handleImageError,
    resetImageError
  };
}

// Export utility functions for standalone use
export { getInitials, getAvatarColor };
